import { db } from './firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  WhereFilterOp,
  DocumentData,
  QueryConstraint,
  DocumentReference,
  CollectionReference
} from 'firebase/firestore';

interface WhereClause {
  field: string;
  operator: WhereFilterOp;
  value: any;
}

interface QueryOptions {
  orderBy?: string;
  where?: WhereClause;
}

interface FirebaseDocument {
  id: string;
  [key: string]: any;
}

export abstract class FirebaseService {
  protected readonly collectionPath: string;
  protected collectionRef: CollectionReference;

  /**
   * Crée une instance du service pour une collection spécifique
   * @param collectionPath - Chemin de la collection
   */
  constructor(collectionPath: string) {
    this.collectionPath = collectionPath;
    this.collectionRef = collection(db, collectionPath);
  }

  /**
   * Récupère tous les documents de la collection
   * @param options - Options de requête
   * @returns Promise avec un tableau de documents
   */
  protected async getAll(options: QueryOptions = {}): Promise<FirebaseDocument[]> {
    try {
      const constraints: QueryConstraint[] = [];
      
      if (options.orderBy) {
        constraints.push(orderBy(options.orderBy));
      }

      if (options.where) {
        constraints.push(where(
          options.where.field, 
          options.where.operator, 
          options.where.value
        ));
      }

      const q = constraints.length > 0 
        ? query(this.collectionRef, ...constraints)
        : this.collectionRef;

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching documents from ${this.collectionPath}:`, error);
      throw error;
    }
  }

  /**
   * Récupère un document spécifique
   * @param id - ID du document
   * @returns Promise avec le document
   */
  protected async getOne(id: string): Promise<FirebaseDocument> {
    try {
      const docRef = doc(db, this.collectionPath, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error(`Error fetching document ${id} from ${this.collectionPath}:`, error);
      throw error;
    }
  }

  /**
   * Ajoute un nouveau document
   * @param data - Données du document
   * @returns Promise avec la référence du document créé
   */
  protected async add(data: DocumentData): Promise<DocumentReference> {
    try {
      const docRef = await addDoc(this.collectionRef, {
        ...data,
        id: null  // sera remplacé par l'ID du document
    });
    
    // Mettre à jour uniquement l'ID
    await this.update(docRef.id, { id: docRef.id });
    
    return docRef;
} catch (error) {
      console.error(`Error adding document to ${this.collectionPath}:`, error);
      throw error;
    }
  }

  /**
   * Met à jour un document
   * @param id - ID du document
   * @param data - Nouvelles données
   */
  protected async update(id: string, data: Partial<DocumentData>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionPath, id);
      await updateDoc(docRef, data);
    } catch (error) {
      console.error(`Error updating document ${id} in ${this.collectionPath}:`, error);
      throw error;
    }
  }

  /**
   * Supprime un document
   * @param id - ID du document
   */
  protected async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionPath, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document ${id} from ${this.collectionPath}:`, error);
      throw error;
    }
  }
}